import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug, fetchGalleryBySlug } from '../lib/cms';
import Metadata from '../components/Metadata';
import GalleryView from '../components/GalleryView';
import ReviewsSlider from '../components/ReviewsSlider';
import ContactForm from '../components/ContactForm';

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);

  const slides = [
    {
      portrait: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-1223414721.jpg",
      landscape: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-520135246.jpg",
      altPortrait: "Professionel havemand udfører havearbejde",
      altLandscape: "Velplejet grøn have og græsplæne"
    },
    {
      portrait: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-1041070742.jpg",
      landscape: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-950758368.jpg",
      altPortrait: "Beskæring af buske og træer",
      altLandscape: "Professionel hækkeklipning"
    },
    {
      portrait: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-1043608090.jpg",
      landscape: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-1315346350.jpg",
      altPortrait: "Totalservice og ejendomsservice",
      altLandscape: "Flot lagt flisebelægning"
    }
  ];

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug('index');
        const gallery = await fetchGalleryBySlug('totalservice');
        setPageData(page);
        setGalleryData(gallery);
      } catch (error) {
        console.error('Error loading home data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[--color-brand-cream]">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-[--color-brand-green-dark]"></i>
          <p className="font-sans text-sm text-[--color-brand-muted] font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  // Fallback metadata if fetch fails
  const title = pageData?.meta_title || "Havemand til haveservice i Gelsted nær Assens, Middelfart, Aarup";
  const description = pageData?.meta_description || "Havemand i Gelsted, Assens, Middelfart og Aarup – professionel hjælp til havearbejde, pleje og vedligeholdelse af din have. Kontakt os";

  return (
    <>
      <Metadata title={title} description={description} />

      {/* Hero Section */}
      <section className="relative bg-[--color-brand-cream] overflow-hidden min-h-[650px] py-16 lg:py-24 px-6 flex items-center">
        {/* Diagonal Sage Backdrop Split (Hero Left) */}
        <div 
          className="absolute inset-y-0 left-0 w-full lg:w-3/5 -skew-x-12 origin-top-left -translate-x-12 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(135deg, rgba(141, 164, 126, 0.35) 0%, rgba(76, 110, 59, 0.15) 100%)' }}
        ></div>

        {/* The "Peeking" Circle (Bottom-Left) */}
        <div 
          className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full z-10 pointer-events-none"
          style={{ backgroundColor: 'rgba(45, 75, 28, 0.15)' }}
        ></div>

        {/* Organic Background Arch (Top Right) */}
        <div 
          className="absolute top-0 right-0 w-[30%] h-[50%] rounded-bl-[150px] z-10 pointer-events-none"
          style={{ backgroundColor: 'rgba(141, 164, 126, 0.12)' }}
        ></div>

        <div className="relative z-20 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column: Editorial Headings */}
          <div className="lg:col-span-6 space-y-6">
            <span className="inline-flex items-center gap-2 font-sans font-bold tracking-widest text-xs uppercase text-[--color-brand-green-dark]">
              <span className="h-1.5 w-1.5 rounded-full bg-[--color-brand-green-dark] animate-pulse"></span>
              Professionel Haveservice
            </span>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl text-[--color-brand-charcoal] font-bold leading-tight">
              Kvalitetsbevidst havemand med fokus på dine behov
            </h1>
            <p className="font-sans text-base text-[--color-brand-muted] max-w-xl leading-relaxed">
              Har du brug for hjælp til at pleje og vedligeholde haven, eller kunne du tænke dig at få etableret en helt ny græsplæne? Hos BP haveservice i Gelsted står vi klar til at hjælpe dig, hvis du har brug for professionel hjælp fra en dygtig havemand i Assens, Middelfart, Aarup eller omegn.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link
                to="/kontakt"
                className="group inline-flex items-center justify-center px-8 py-3.5 rounded-full bg-white text-[--color-brand-green-dark] border border-[--color-brand-green-dark]/20 hover:bg-white hover:text-[--color-brand-green-medium] hover:border-[--color-brand-green-medium] transition-all duration-300 text-sm font-semibold tracking-wide shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
              >
                <span>Kontakt os i dag</span>
                <i className="fa-solid fa-arrow-right ml-2.5 text-xs transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/ydelser"
                className="inline-flex items-center justify-center px-8 py-3.5 rounded-full border border-[--color-brand-sand] text-[--color-brand-charcoal] hover:bg-[--color-brand-sand]/40 transition-all duration-200 text-sm font-semibold tracking-wide hover:-translate-y-0.5 cursor-pointer"
              >
                Læs mere
              </Link>
            </div>
          </div>

          {/* Right Column: Interactive composition panel layering the matrix dots, the leaf portrait container, and the overlapping leaf landscape */}
          <div className="lg:col-span-6 relative flex flex-col items-center justify-center py-8 w-full max-w-xl mx-auto overflow-visible">
            {/* Matrix Dot Pattern (Background Texture) */}
            <div className="absolute -top-4 -right-4 grid grid-cols-6 gap-3 w-28 opacity-30 text-[--color-brand-green-dark] pointer-events-none z-0">
              {Array.from({ length: 24 }).map((_, i) => (
                <span key={i} className="w-1.5 h-1.5 rounded-full bg-current"></span>
              ))}
            </div>

            {/* Slider Container */}
            <div className="relative w-full h-[360px] sm:h-[460px] lg:h-[480px] overflow-visible flex items-center justify-center lg:justify-start">
              {slides.map((slide, index) => {
                const isActive = index === activeSlide;
                return (
                  <div
                    key={index}
                    className={`absolute inset-0 w-full h-full transition-all duration-700 ease-in-out ${
                      isActive
                        ? "opacity-100 translate-x-0 scale-100 pointer-events-auto z-10"
                        : "opacity-0 translate-x-4 scale-95 pointer-events-none z-0"
                    }`}
                  >
                    {/* Left Portrait Leaf Card */}
                    <div className="absolute left-2 sm:left-8 lg:left-0 top-4 w-[180px] sm:w-[240px] lg:w-[260px] h-[280px] sm:h-[360px] lg:h-[400px] bg-[--color-brand-green-light]/20 rounded-tl-[100px] rounded-br-[100px] rounded-tr-3xl rounded-bl-3xl overflow-hidden shadow-xl border-4 border-white/90 transition-transform duration-500 hover:scale-[1.02]">
                      <img
                        src={slide.portrait}
                        className="object-cover w-full h-full"
                        alt={slide.altPortrait}
                      />
                    </div>

                    {/* Right Landscape Leaf Card (Overlapping & Overflowing to the right) */}
                    <div className="absolute right-2 sm:right-8 lg:-right-12 bottom-4 w-[220px] sm:w-[300px] lg:w-[340px] h-[150px] sm:h-[200px] lg:h-[230px] rounded-br-[100px] rounded-tl-3xl rounded-tr-3xl rounded-bl-3xl overflow-hidden shadow-2xl border-4 border-white/90 transition-transform duration-500 hover:scale-[1.02] z-20">
                      <img
                        src={slide.landscape}
                        className="object-cover w-full h-full"
                        alt={slide.altLandscape}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Navigation Arrows (Prev/Next) */}
              <button
                onClick={() => setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length)}
                className="absolute left-2 lg:-left-16 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 hover:bg-white text-[--color-brand-green-dark] shadow-md hover:shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 z-30 cursor-pointer"
                aria-label="Forrige billede"
              >
                <i className="fa-solid fa-chevron-left text-sm"></i>
              </button>
              <button
                onClick={() => setActiveSlide((prev) => (prev + 1) % slides.length)}
                className="absolute right-2 lg:-right-16 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 hover:bg-white text-[--color-brand-green-dark] shadow-md hover:shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 z-30 cursor-pointer"
                aria-label="Næste billede"
              >
                <i className="fa-solid fa-chevron-right text-sm"></i>
              </button>
            </div>

            {/* Pagination Indicator Dots below to tie the visual components together */}
            <div className="flex gap-3 justify-center mt-4 z-20">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveSlide(index)}
                  className={`h-3 rounded-full transition-all duration-300 cursor-pointer ${
                    index === activeSlide
                      ? "w-8 bg-[--color-brand-green-dark]"
                      : "w-3 bg-[--color-brand-green-light]/50 hover:bg-[--color-brand-green-light]"
                  }`}
                  aria-label={`Gå til slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Clickable Contact Boxes Section */}
      <section className="bg-white py-12 border-y border-[--color-brand-sand]">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Phone Box */}
          <a
            href="tel:30441611"
            className="flex items-center gap-6 p-6 rounded-3xl bg-[--color-brand-cream]/40 border border-[--color-brand-sand] hover:border-[--color-brand-green-light] hover:bg-[--color-brand-cream]/60 transition-all duration-300 group"
          >
            <div className="w-14 h-14 rounded-full bg-[--color-brand-sage-pale] text-[--color-brand-green-dark] flex items-center justify-center text-xl group-hover:scale-110 transition-transform duration-300">
              <i className="fa-solid fa-phone"></i>
            </div>
            <div>
              <p className="font-sans text-xs font-bold uppercase tracking-wider text-[--color-brand-muted]">
                Ring til os direkte
              </p>
              <p className="font-display text-base sm:text-lg md:text-2xl font-bold text-[--color-brand-charcoal] whitespace-nowrap">
                30 44 16 11
              </p>
            </div>
          </a>

          {/* Email Box */}
          <a
            href="mailto:post@bp-haveservice.dk"
            className="flex items-center gap-6 p-6 rounded-3xl bg-[--color-brand-cream]/40 border border-[--color-brand-sand] hover:border-[--color-brand-green-light] hover:bg-[--color-brand-cream]/60 transition-all duration-300 group"
          >
            <div className="w-14 h-14 rounded-full bg-[--color-brand-sage-pale] text-[--color-brand-green-dark] flex items-center justify-center text-xl group-hover:scale-110 transition-transform duration-300">
              <i className="fa-solid fa-envelope"></i>
            </div>
            <div>
              <p className="font-sans text-xs font-bold uppercase tracking-wider text-[--color-brand-muted]">
                Send os en e-mail
              </p>
              <p className="font-display text-base sm:text-lg md:text-2xl font-bold text-[--color-brand-charcoal] whitespace-nowrap">
                post@bp-haveservice.dk
              </p>
            </div>
          </a>
        </div>
      </section>

      {/* Presentation of Services (Ydelser) */}
      <section className="py-20 bg-[--color-brand-cream]/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
            <span className="font-sans font-bold tracking-widest text-xs uppercase text-[--color-brand-green-dark]">
              Vores Kompetencer
            </span>
            <h2 className="font-display text-2xl md:text-4xl lg:text-5xl font-bold text-[--color-brand-charcoal]">
              Hvad kan vi tilbyde?
            </h2>
            <p className="font-sans text-sm text-[--color-brand-muted] leading-relaxed">
              Se mere om hvad vi kan gøre for dig her. Vi tilbyder professionel hjælp til en lang række opgaver.
            </p>
          </div>

          {/* Service Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Card 1: Græsslåning */}
            <ServiceCard
              title="Græsslåning"
              description="Professionel slåning og pleje af din græsplæne, så den altid fremstår tæt, grøn og velplejet."
              imgUrl="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=85,format=auto/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-520135246.jpg"
              href="/ydelser/graesslaaning"
              icon="fa-solid fa-seedling"
            />

            {/* Card 2: Ny terrasse */}
            <ServiceCard
              title="Ny terrasse"
              description="Etablering af smukke og holdbare terrasser i fliser eller træ, skræddersyet til din have."
              imgUrl="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=85,format=auto/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-1315346350.jpg"
              href="/ydelser/ny-terrasse"
              icon="fa-solid fa-house-chimney-window"
            />

            {/* Card 3: Ny indkørsel */}
            <ServiceCard
              title="Ny indkørsel"
              description="Professionel belægning af indkørsler med fokus på stabilitet, korrekt fald og flot design."
              imgUrl="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=85,format=auto/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-1041070742.jpg"
              href="/ydelser/ny-indkoersel"
              icon="fa-solid fa-car"
            />

            {/* Card 4: Etablering af græsplæner */}
            <ServiceCard
              title="Etablering af græsplæner"
              description="Hjælp to såning af nyt græs eller lægning af rullegræs for en hurtig og flot grøn plæne."
              imgUrl="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=85,format=auto/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-1223414721.jpg"
              href="/ydelser/etablering-af-graesplaener"
              icon="fa-solid fa-leaf"
            />
          </div>
        </div>
      </section>

      {/* Local Trust & Experience Section */}
      <section className="pt-20 pb-0 lg:pb-32 bg-white border-y border-[--color-brand-sand]">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Image with Overlapping Badges */}
          <div className="relative flex items-center justify-center min-h-[480px] md:min-h-[550px] w-full max-w-xl mx-auto">
            {/* Decorative Leaf Vector (Top Left) */}
            <div className="absolute -top-20 -left-16 w-48 h-48 md:w-56 md:h-56 z-0 pointer-events-none">
              <img 
                src="https://assets.auxodev.dk/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/leaf-decor.png" 
                alt="Bladdekorationsdetalje" 
                className="w-full h-full object-contain opacity-50"
              />
            </div>

            {/* Primary Image (Portrait Leaf Shape) */}
            <div className="relative z-10 w-[75%] ml-auto aspect-[3/4] overflow-hidden shadow-xl rounded-tl-[100px] rounded-br-[100px] rounded-tr-3xl rounded-bl-3xl border-4 border-white">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-950758368.jpg"
                alt="Erfaren havemand i Gelsted"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Secondary Overlapping Image (Bottom Left) */}
            <div className="absolute -bottom-6 left-0 w-[48%] aspect-square z-20 overflow-hidden shadow-2xl border-4 border-white rounded-tr-[80px] rounded-bl-[80px] rounded-tl-2xl rounded-br-2xl">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=85,format=auto/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-520135246.jpg"
                alt="Velplejet grøn have"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Decorative Dot Matrix (Bottom Right) */}
            <div className="absolute -bottom-8 right-12 z-0 opacity-30 hidden sm:grid grid-cols-5 gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[--color-brand-green-dark]"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-[--color-brand-green-dark]"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-[--color-brand-green-dark]"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-[--color-brand-green-dark]"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-[--color-brand-green-dark]"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-[--color-brand-green-dark]"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-[--color-brand-green-dark]"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-[--color-brand-green-dark]"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-[--color-brand-green-dark]"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-[--color-brand-green-dark]"></div>
            </div>

            {/* Overlapping Badge */}
            <div className="absolute -bottom-10 -right-2 bg-[#F4F6EE] text-[--color-brand-green-dark] p-5 rounded-2xl shadow-lg flex flex-col items-center justify-center text-center border border-[--color-brand-green-light]/20 z-30">
              <span className="font-display text-2xl font-bold">100%</span>
              <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-[--color-brand-charcoal]/80">
                Kundetilfredshed
              </span>
            </div>
          </div>

          {/* Right: Content */}
          <div className="space-y-6 md:pt-12 lg:pt-0 pb-5 lg:pb-0">
            <span className="font-sans font-bold tracking-widest text-xs uppercase text-[--color-brand-green-dark]">
              Lokal Erfaring & Troværdighed
            </span>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-[--color-brand-charcoal]">
              Høj kvalitet og god service
            </h2>
            <p className="font-sans text-sm text-[--color-brand-muted] leading-relaxed">
              Når du vælger os som samarbejdspartner, kan du forvente løsninger i høj kvalitet – hver gang. Vores ansatte har de rette kompetencer til at udføre den opgave, som du hyrer os til, og vi går aldrig på kompromis med kvaliteten i vores arbejde.
            </p>
            <p className="font-sans text-sm text-[--color-brand-muted] leading-relaxed">
              Ud over god kvalitet kan du forvente fremragende service, når du lader os stå for din opgave. Det betyder alt for os, at vores kunder er tilfredse med vores arbejde. Vi guider dig gennem forløbet fra start til slut, og du er altid velkommen til at spørge, hvis du har det mindste spørgsmål.
            </p>
            <div className="pt-4">
              <Link
                to="/om-os"
                className="inline-flex items-center gap-2 font-sans text-sm font-bold text-[--color-brand-green-dark] hover:text-[--color-brand-green-medium] transition-colors"
              >
                Læs mere om os
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-[--color-brand-cream]/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
            <span className="font-sans font-bold tracking-widest text-xs uppercase text-[--color-brand-green-dark]">
              Anmeldelser
            </span>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-[--color-brand-charcoal]">
              Hvad siger vores kunder?
            </h2>
            <p className="font-sans text-sm text-[--color-brand-muted]">
              Læs anmeldelser fra vores tilfredse kunder i Gelsted og omegn.
            </p>
          </div>
          <ReviewsSlider />
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-white border-t border-[--color-brand-sand]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-16">
            <div className="space-y-4 max-w-xl">
              <span className="font-sans font-bold tracking-widest text-xs uppercase text-[--color-brand-green-dark]">
                Vores Arbejde
              </span>
              <h2 className="font-display text-2xl md:text-4xl font-bold text-[--color-brand-charcoal]">
                Vores senest udførte arbejde
              </h2>
              <p className="font-sans text-sm text-[--color-brand-muted]">
                Vi udfører en bred vifte af opgaver – se vores seneste referencer her.
              </p>
            </div>
            <Link
              to="/galleri"
              className="inline-flex items-center justify-center px-6 py-2.5 rounded-full border border-[--color-brand-sand] text-[--color-brand-charcoal] hover:bg-[--color-brand-cream] transition-colors text-sm font-semibold tracking-wide"
            >
              Se hele galleriet
            </Link>
          </div>

          {galleryData && (
            <GalleryView images={galleryData.images} title={galleryData.title} />
          )}
        </div>
      </section>

      {/* Contact Section with Form */}
      <section className="py-20 bg-[--color-brand-cream]/40 border-t border-[--color-brand-sand]">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Info */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <span className="font-sans font-bold tracking-widest text-xs uppercase text-[--color-brand-green-dark]">
                Kontakt Os
              </span>
              <h2 className="font-display text-2xl md:text-4xl font-bold text-[--color-brand-charcoal]">
                Vil du vide mere?
              </h2>
              <p className="font-sans text-sm text-[--color-brand-muted] leading-relaxed">
                Finder du ikke nok informationer om din ønskede opgave her på vores hjemmeside, er du altid meget velkommen til at kontakte os til en uforpligtende snak og eventuelt et tilbud på opgaven.
              </p>
            </div>

            <div className="space-y-6">
              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[--color-brand-green-dark]/10 text-[--color-brand-green-dark] flex items-center justify-center text-sm flex-shrink-0">
                  <i className="fa-solid fa-phone"></i>
                </div>
                <div>
                  <h4 className="font-sans font-bold text-sm text-[--color-brand-charcoal]">
                    Ring til os
                  </h4>
                  <a href="tel:30441611" className="font-sans text-sm text-[--color-brand-muted] hover:text-[--color-brand-green-dark] transition-colors whitespace-nowrap">
                    30 44 16 11
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[--color-brand-green-dark]/10 text-[--color-brand-green-dark] flex items-center justify-center text-sm flex-shrink-0">
                  <i className="fa-solid fa-envelope"></i>
                </div>
                <div>
                  <h4 className="font-sans font-bold text-sm text-[--color-brand-charcoal]">
                    Send en mail
                  </h4>
                  <a href="mailto:post@bp-haveservice.dk" className="font-sans text-sm text-[--color-brand-muted] hover:text-[--color-brand-green-dark] transition-colors whitespace-nowrap">
                    post@bp-haveservice.dk
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[--color-brand-green-dark]/10 text-[--color-brand-green-dark] flex items-center justify-center text-sm flex-shrink-0">
                  <i className="fa-solid fa-location-dot"></i>
                </div>
                <div>
                  <h4 className="font-sans font-bold text-sm text-[--color-brand-charcoal]">
                    Adresse
                  </h4>
                  <p className="font-sans text-sm text-[--color-brand-muted]">
                    Søndergade 111, 5591 Gelsted
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Form */}
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}

function ServiceCard({ title, description, imgUrl, href, icon, className = "" }) {
  return (
    <div className={`flex flex-col bg-white rounded-3xl overflow-hidden border border-[--color-brand-sand] shadow-xs group ${className}`}>
      {/* Image Container */}
      <div className="relative aspect-16/10 overflow-hidden bg-[--color-brand-sand]">
        <img
          src={imgUrl}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {/* Floating Icon Badge */}
        <div className="absolute bottom-4 left-6 w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center border border-[--color-brand-sand] z-10">
          <i className={`${icon} text-[--color-brand-green-dark] text-base`}></i>
        </div>
      </div>

      {/* Content */}
      <div className="p-8 pt-8 flex-1 flex flex-col justify-between">
        <div className="space-y-3">
          <h3 className="font-display text-xl md:text-2xl text-[--color-brand-charcoal] font-bold group-hover:text-[--color-brand-green-dark] transition-colors">
            {title}
          </h3>
          <p className="font-sans text-sm text-[--color-brand-muted] leading-relaxed">
            {description}
          </p>
        </div>
        <div className="pt-6">
          <Link
            to={href}
            className="inline-flex items-center font-sans text-xs font-bold text-[--color-brand-green-dark] tracking-wider uppercase hover:text-[--color-brand-green-medium] transition-colors"
          >
            Læs mere <span className="ml-1 text-[10px]">→</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
